import type {Writable} from 'svelte/store';
import {writable, get} from 'svelte/store';
import type {
  BaseEntity,
  Entity,
  EntitySubType,
  EntityType,
} from 'Store/investorProfile/entityStore';
import {defaultEntity} from 'Store/investorProfile/entityStore';
import {userEmail} from 'Store/investorProfile/userEmailStore';
import type {CaAccreditedOptionValue} from 'Types/investorProfile/accreditedOptionTypes';
import {getHoldingTypeLabel} from 'Utility/investorProfile/investorProfileForm';
import {dateMaskPlaceholder} from 'Utility/commons/dateField';
import {getValidTin} from 'Utility/checkoutFlow/contactInformation/tinMask';

export type Address = {
  street_address?: string;
  unit2?: string;
  city?: string;
  country?: string;
  region?: string;
  postal_code?: string;
};

type BaseEntityPayload = {
  email: string;
  us_accredited_category?: string;
  suffix?: string;
  date_of_birth?: string;
  date?: string;
  phone_number?: string;
  income?: number;
  net_worth?: number;
  reg_cf_prior_offerings_amount?: number;
  ca_accredited_investor?: CaAccreditedOptionValue;
} & Address;

export type IndividualProfilePayload = BaseEntityPayload & {
  first_name?: string;
  last_name?: string;
  taxpayer_id?: string;
};

type JointProfilePayload = IndividualProfilePayload & {
  joint_type: EntitySubType;
  joint_holder_first_name: string;
  joint_holder_last_name: string;
  joint_holder_suffix: string;
  joint_holder_date_of_birth: string;
  joint_holder_taxpayer_id: string;
  joint_holder_country: string;
  joint_holder_street_address: string;
  joint_holder_unit2: string;
  joint_holder_city: string;
  joint_holder_region: string;
  joint_holder_postal_code: string;
};

type BeneficialOwnerPayload = {
  index: number;
  _delete?: boolean;
  first_name?: string;
  last_name?: string;
  taxpayer_id?: string;
  phone_number?: string;
} & Address;

type TrusteePayload = BeneficialOwnerPayload;

export type TrustProfilePayload = BaseEntityPayload & {
  name: string;
  trustees?: TrusteePayload[];
};

export type CorporationProfilePayload = BaseEntityPayload & {
  name: string;
  business_number: string;
  signing_officer_title: string;
  signing_officer_first_name: string;
  signing_officer_last_name: string;
  signing_officer_suffix: string;
  signing_officer_country: string;
  signing_officer_street_address: string;
  signing_officer_unit2: string;
  signing_officer_city: string;
  signing_officer_region: string;
  signing_officer_postal_code: string;
  signing_officer_date_of_birth: string;
  signing_officer_taxpayer_id: string;
  signing_officer_phone_number: string;
  beneficial_owners?: BeneficialOwnerPayload[];
};

export type ManagedProfilePayload = BaseEntityPayload & {
  name: string;
  taxpayer_id?: string;
  confirmation?: boolean;
  provider_email?: string;
  beneficiary_account_number: string;
  beneficiary_first_name: string;
  beneficiary_last_name: string;
  beneficiary_suffix: string;
  beneficiary_country: string;
  beneficiary_street_address: string;
  beneficiary_unit2: string;
  beneficiary_city: string;
  beneficiary_region: string;
  beneficiary_postal_code: string;
  beneficiary_date_of_birth: string;
  beneficiary_taxpayer_id: string;
  beneficiary_phone_number: string;
};

const mapAddressEntity = (entity: BaseEntity): Address => {
  return {
    street_address: entity.addressLine1,
    unit2: entity.addressLine2,
    city: entity.city,
    country: entity.countryCode,
    region: entity.regionCode,
    postal_code: entity.postalCode,
  };
};

// Note that caAccreditedCategory is called ca_accredited_investor in the payload
const mapBasicEntity = (entity: BaseEntity): BaseEntityPayload => {
  return {
    suffix: entity.suffix,
    date_of_birth: entity.dateOfBirth,
    phone_number: entity.phoneNumber,
    email: entity.email,
    us_accredited_category: entity.usAccreditedCategory,
    ca_accredited_investor: entity.caAccreditedCategory,
    ...mapAddressEntity(entity),
  };
};

export const mapIndividualProfile = (
  entity: Entity
): IndividualProfilePayload => {
  return {
    ...mapBasicEntity(entity),
    first_name: entity.firstName,
    last_name: entity.lastName,
    taxpayer_id: getValidTin(entity),
    income: entity.income,
    net_worth: entity.netWorth,
    reg_cf_prior_offerings_amount: entity.regCFPriorOfferingsAmount,
  };
};

export const mapJointProfile = (entity: Entity): JointProfilePayload => {
  const jointHolder = get(entity.jointHolder);
  const subTypeCode = jointHolder?.subTypeCode || entity?.subTypeCode;

  return {
    ...mapIndividualProfile(entity),
    joint_type: subTypeCode,
    joint_holder_first_name: jointHolder.firstName,
    joint_holder_last_name: jointHolder.lastName,
    joint_holder_suffix: jointHolder.suffix,
    joint_holder_date_of_birth: jointHolder.dateOfBirth,
    joint_holder_taxpayer_id: getValidTin(jointHolder),
    joint_holder_country: jointHolder.countryCode,
    joint_holder_street_address: jointHolder.addressLine1,
    joint_holder_unit2: jointHolder.addressLine2,
    joint_holder_city: jointHolder.city,
    joint_holder_region: jointHolder.regionCode,
    joint_holder_postal_code: jointHolder.postalCode,
  };
};

export const maxBeneficialOwners = 4;
export const maxTrustees = 4;

const clearDobPlaceholder = (dob: string): string =>
  dob === dateMaskPlaceholder ? '' : dob;

export const mapCorporationProfile = (
  entity: Entity
): CorporationProfilePayload => {
  const signingOfficer = get(entity.signingOfficer);
  const beneficialOwners = get(entity.beneficialOwners);

  return {
    name: entity.firstName,
    email: entity.email || get(userEmail),
    street_address: entity.addressLine1,
    unit2: entity.addressLine2,
    city: entity.city,
    country: entity.countryCode,
    region: entity.regionCode,
    postal_code: entity.postalCode,
    business_number: entity.tin,
    phone_number: entity.phoneNumber,
    signing_officer_first_name: signingOfficer?.firstName,
    signing_officer_last_name: signingOfficer?.lastName,
    signing_officer_title: signingOfficer?.title,
    signing_officer_suffix: signingOfficer?.suffix,
    signing_officer_street_address: signingOfficer?.addressLine1,
    signing_officer_unit2: signingOfficer?.addressLine2,
    signing_officer_country: signingOfficer?.countryCode,
    signing_officer_city: signingOfficer?.city,
    signing_officer_region: signingOfficer?.regionCode,
    signing_officer_postal_code: signingOfficer?.postalCode,
    signing_officer_date_of_birth: clearDobPlaceholder(
      signingOfficer?.dateOfBirth
    ),
    signing_officer_taxpayer_id: getValidTin(signingOfficer),
    signing_officer_phone_number: signingOfficer?.phoneNumber,
    beneficial_owners: Array.from({length: maxBeneficialOwners}, (_, index) => {
      if (index < beneficialOwners.length) {
        const owner = get(beneficialOwners[index]);

        return {
          index,
          first_name: owner?.firstName,
          last_name: owner?.lastName,
          country: owner?.countryCode,
          street_address: owner?.addressLine1,
          unit2: owner?.addressLine2,
          city: owner?.city,
          region: owner?.regionCode,
          postal_code: owner?.postalCode,
          date_of_birth: clearDobPlaceholder(owner?.dateOfBirth),
          taxpayer_id: getValidTin(owner),
          phone_number: owner?.phoneNumber,
          _delete: false,
        };
      } else {
        return {index, _delete: true};
      }
    }),
  };
};

export const mapTrustProfile = (entity: Entity): TrustProfilePayload => {
  const trustees = get(entity.trustees);

  return {
    name: entity.firstName,
    email: entity.email || get(userEmail),
    street_address: entity.addressLine1,
    unit2: entity.addressLine2,
    city: entity.city,
    country: entity.countryCode,
    region: entity.regionCode,
    postal_code: entity.postalCode,
    phone_number: entity.phoneNumber,
    date: entity.date,
    trustees: Array.from({length: maxTrustees}, (_, index) => {
      if (index < trustees.length) {
        const trustee = get(trustees[index]);

        return {
          index,
          first_name: trustee?.firstName,
          last_name: trustee?.lastName,
          country: trustee?.countryCode,
          street_address: trustee?.addressLine1,
          unit2: trustee?.addressLine2,
          city: trustee?.city,
          region: trustee?.regionCode,
          postal_code: trustee?.postalCode,
          date_of_birth: trustee?.dateOfBirth,
          taxpayer_id: getValidTin(trustee),
          phone_number: trustee?.phoneNumber,
          _delete: false,
        };
      } else {
        return {index, _delete: true};
      }
    }),
  };
};

export const mapManagedProfile = (entity: Entity): ManagedProfilePayload => {
  const beneficiary = get(entity.beneficiary);

  return {
    name: entity.firstName,
    email: entity.email || get(userEmail),
    street_address: entity.addressLine1,
    unit2: entity.addressLine2,
    city: entity.city,
    country: entity.countryCode,
    region: entity.regionCode,
    postal_code: entity.postalCode,
    taxpayer_id: entity.tin,
    phone_number: entity.phoneNumber,
    confirmation: entity.confirmation,
    provider_email: entity.emailAlternate,
    beneficiary_account_number: entity?.accountNumber,
    beneficiary_first_name: beneficiary?.firstName,
    beneficiary_last_name: beneficiary?.lastName,
    beneficiary_suffix: beneficiary?.suffix,
    beneficiary_street_address: beneficiary?.addressLine1,
    beneficiary_unit2: beneficiary?.addressLine2,
    beneficiary_country: beneficiary?.countryCode,
    beneficiary_city: beneficiary?.city,
    beneficiary_region: beneficiary?.regionCode,
    beneficiary_postal_code: beneficiary?.postalCode,
    beneficiary_date_of_birth: clearDobPlaceholder(beneficiary?.dateOfBirth),
    beneficiary_taxpayer_id: getValidTin(beneficiary),
    beneficiary_phone_number: beneficiary?.phoneNumber,
  };
};

type DefaultProfileResponse = {
  user_id?: number;
  email?: EntityType;
  type?: EntityType;
  complete?: boolean;
  us_accredited_category?: string;
  ca_accredited_investor?: CaAccreditedOptionValue;
};

export type EntityResponse = DefaultProfileResponse &
  IndividualProfileResponse &
  JointProfileResponse &
  CorporationProfileResponse &
  TrustProfileResponse &
  ManagedProfileResponse;

export type BasicEntityResponse = {
  first_name?: string;
  last_name?: string;
  suffix?: string;
  date_of_birth?: string;
  taxpayer_id?: string;
  phone_number?: string;
  income?: number;
  net_worth?: number;
  reg_cf_prior_offerings_amount?: number;
  ca_accredited_investor?: string;
  address: Address;
};

type IndividualProfileResponse = {
  account_holder: BasicEntityResponse;
};

type JointProfileResponse = {
  primary_holder?: BasicEntityResponse;
  joint_holder?: BasicEntityResponse;
  joint_type?: EntitySubType;
};

type CorporationProfileResponse = {
  corporation?: {
    name: string;
    business_number: string;
    phone_number: string;
    income?: string;
    net_worth?: string;
    reg_cf_prior_offerings_amount?: string;
    address: Address;
  };
  signing_officer?: {title?: string} & BasicEntityResponse;
  beneficial_owners?: BasicEntityResponse[];
};

type TrustProfileResponse = {
  trust_holder?: {
    name: string;
    date: string;
    phone_number: string;
    income?: string;
    net_worth?: string;
    reg_cf_prior_offerings_amount?: string;
    address: Address;
  };
  trustees?: BasicEntityResponse[];
};

type ManagedProfileResponse = {
  provider?: {
    name: string;
    email: string;
    taxpayer_id: string;
    confirmation: string;
    income?: string;
    net_worth?: string;
    reg_cf_prior_offerings_amount?: string;
    address: Address;
  };
  beneficiary?: BeneficiaryProfileResponse;
};

type BeneficiaryProfileResponse = BasicEntityResponse & {
  account_number: string;
};

export const buildIndividualBody = (profile: EntityResponse): Entity => {
  const {account_holder} = profile;

  return {
    ...defaultEntity(),
    firstName: account_holder?.first_name,
    lastName: account_holder?.last_name,
    suffix: account_holder?.suffix,
    dateOfBirth: account_holder?.date_of_birth,
    tin: account_holder?.taxpayer_id,
    addressLine1: account_holder?.address?.street_address,
    addressLine2: account_holder?.address?.unit2,
    city: account_holder?.address?.city,
    region: account_holder?.address?.region,
    regionCode: account_holder?.address?.region,
    country: account_holder?.address?.country,
    countryCode: account_holder?.address?.country,
    postalCode: account_holder?.address?.postal_code,
    type: profile.type,
    phoneNumber: account_holder.phone_number,
    income: account_holder.income,
    netWorth: account_holder.net_worth,
    regCFPriorOfferingsAmount: account_holder.reg_cf_prior_offerings_amount,
    email: profile.email,
    usAccreditedCategory: profile.us_accredited_category,
    caAccreditedCategory: profile.ca_accredited_investor,
    complete: profile.complete,
  };
};

export const buildJointBody = (profile: EntityResponse): Entity => {
  const {primary_holder, joint_holder} = profile;

  const jointHolderBody = {
    firstName: joint_holder?.first_name,
    lastName: joint_holder?.last_name,
    suffix: joint_holder?.suffix,
    dateOfBirth: joint_holder?.date_of_birth,
    tin: joint_holder?.taxpayer_id,
    addressLine1: joint_holder?.address?.street_address,
    addressLine2: joint_holder?.address?.unit2,
    city: joint_holder?.address?.city,
    country: joint_holder?.address?.country,
    postalCode: joint_holder?.address?.postal_code,
    region: joint_holder?.address?.region,
    countryCode: joint_holder?.address?.country,
    regionCode: joint_holder?.address?.region,
  };

  return {
    ...defaultEntity(),
    type: profile.type,
    email: profile.email,
    firstName: primary_holder.first_name,
    lastName: primary_holder.last_name,
    suffix: primary_holder.suffix,
    dateOfBirth: primary_holder.date_of_birth,
    tin: primary_holder.taxpayer_id,
    phoneNumber: primary_holder.phone_number,
    income: primary_holder.income,
    netWorth: primary_holder.net_worth,
    regCFPriorOfferingsAmount: primary_holder.reg_cf_prior_offerings_amount,
    addressLine1: primary_holder.address.street_address,
    addressLine2: primary_holder.address.unit2,
    city: primary_holder.address.city,
    country: primary_holder.address.country,
    countryCode: primary_holder.address.country,
    regionCode: primary_holder.address.region,
    postalCode: primary_holder.address.postal_code,
    region: primary_holder.address.region,
    usAccreditedCategory: profile.us_accredited_category,
    caAccreditedCategory: profile.ca_accredited_investor,
    complete: profile.complete,
    subType: getHoldingTypeLabel(profile.joint_type),
    subTypeCode: profile.joint_type,
    jointHolder: writable<BaseEntity>(jointHolderBody),
  };
};

export const allValuesUndefined = (obj: Object): boolean => {
  for (const key in obj) {
    if (typeof obj[key] === 'object') {
      if (!allValuesUndefined(obj[key])) {
        return false;
      }
    } else if (obj[key]) {
      return false;
    }
  }

  return true;
};

export const buildCorporationEntityBody = (profile: EntityResponse): Entity => {
  const {corporation, signing_officer, beneficial_owners = []} = profile;

  // This is dropping all entities that are empty.
  const existingBeneficialOwners = beneficial_owners?.filter(
    obj => !allValuesUndefined(obj)
  ) as any[];
  const beneficialOwners = writable(
    buildBeneficialOwnersBody(existingBeneficialOwners)
  );
  const signingOfficerBody = {
    firstName: signing_officer?.first_name,
    lastName: signing_officer?.last_name,
    title: signing_officer?.title,
    suffix: signing_officer?.suffix,
    dateOfBirth: signing_officer?.date_of_birth,
    tin: signing_officer?.taxpayer_id,
    phoneNumber: signing_officer?.phone_number,
    addressLine1: signing_officer?.address?.street_address,
    addressLine2: signing_officer?.address?.unit2,
    city: signing_officer?.address?.city,
    country: signing_officer?.address?.country,
    countryCode: signing_officer?.address?.country,
    postalCode: signing_officer?.address?.postal_code,
    region: signing_officer?.address?.region,
    regionCode: signing_officer?.address?.region,
  };

  return {
    type: profile.type,
    email: profile.email,
    firstName: corporation?.name,
    lastName: '',
    tin: corporation?.business_number,
    phoneNumber: corporation?.phone_number,
    addressLine1: corporation?.address.street_address,
    addressLine2: corporation?.address.unit2,
    city: corporation?.address.city,
    country: corporation?.address.country,
    countryCode: corporation?.address.country,
    regionCode: corporation?.address.region,
    postalCode: corporation?.address.postal_code,
    region: corporation?.address.region,
    usAccreditedCategory: profile.us_accredited_category,
    caAccreditedCategory: profile.ca_accredited_investor,
    complete: profile.complete,
    subType: getHoldingTypeLabel(profile.joint_type),
    subTypeCode: profile.joint_type,
    signingOfficer: writable<BaseEntity>(signingOfficerBody),
    beneficialOwners: beneficialOwners,
  };
};

export const buildBeneficialOwnersBody = (
  beneficialOwners: BasicEntityResponse[]
): Writable<BaseEntity>[] => {
  // This is ensuring atleast 1 beneficial owner form is present.
  if (beneficialOwners.length === 0)
    beneficialOwners.push({} as BasicEntityResponse);

  return beneficialOwners.map(owner => {
    const {address} = owner;
    return writable<BaseEntity>({
      firstName: owner?.first_name,
      lastName: owner?.last_name,
      dateOfBirth: owner?.date_of_birth,
      tin: owner?.taxpayer_id,
      phoneNumber: owner?.phone_number,
      addressLine1: address?.street_address,
      addressLine2: address?.unit2,
      city: address?.city,
      country: address?.country,
      countryCode: address?.country,
      postalCode: address?.postal_code,
      region: address?.region,
      regionCode: address?.region,
    });
  });
};

export const buildTrustEntityBody = (profile: EntityResponse): Entity => {
  const {trust_holder, trustees = []} = profile;

  // This is dropping all entities that are empty.
  const existingTrustees = trustees.filter(
    obj => !allValuesUndefined(obj)
  ) as any[];
  const trusteesStore = writable(buildTrusteesBody(existingTrustees));

  return {
    type: profile.type,
    email: profile.email,
    firstName: trust_holder?.name,
    lastName: '',
    tin: '',
    phoneNumber: trust_holder?.phone_number,
    addressLine1: trust_holder?.address.street_address,
    addressLine2: trust_holder?.address.unit2,
    city: trust_holder?.address.city,
    country: trust_holder?.address.country,
    countryCode: trust_holder?.address.country,
    regionCode: trust_holder?.address.region,
    postalCode: trust_holder?.address.postal_code,
    region: trust_holder?.address.region,
    date: trust_holder?.date,
    usAccreditedCategory: profile.us_accredited_category,
    caAccreditedCategory: profile.ca_accredited_investor,
    complete: profile.complete,
    trustees: trusteesStore,
  };
};

export const buildTrusteesBody = (
  trustees: BasicEntityResponse[]
): Writable<BaseEntity>[] => {
  // This is ensuring atleast 1 trustee form is present.
  if (trustees.length === 0) trustees.push({} as BasicEntityResponse);

  return trustees.map(trustee => {
    const {address} = trustee;
    return writable<BaseEntity>({
      firstName: trustee?.first_name,
      lastName: trustee?.last_name,
      dateOfBirth: trustee?.date_of_birth,
      tin: trustee?.taxpayer_id,
      phoneNumber: trustee?.phone_number,
      addressLine1: address?.street_address,
      addressLine2: address?.unit2,
      city: address?.city,
      country: address?.country,
      countryCode: address?.country,
      postalCode: address?.postal_code,
      region: address?.region,
      regionCode: address?.region,
    });
  });
};

export const buildManagedEntityBody = (profile: EntityResponse): Entity => {
  const {provider, beneficiary} = profile;

  const beneficiaryBody = {
    firstName: beneficiary?.first_name,
    lastName: beneficiary?.last_name,
    suffix: beneficiary?.suffix,
    dateOfBirth: beneficiary?.date_of_birth,
    tin: beneficiary?.taxpayer_id,
    phoneNumber: beneficiary?.phone_number,
    addressLine1: beneficiary?.address?.street_address,
    addressLine2: beneficiary?.address?.unit2,
    city: beneficiary?.address?.city,
    country: beneficiary?.address?.country,
    countryCode: beneficiary?.address?.country,
    postalCode: beneficiary?.address?.postal_code,
    region: beneficiary?.address?.region,
    regionCode: beneficiary?.address?.region,
  };

  return {
    type: profile.type,
    email: profile.email,
    accountNumber: beneficiary?.account_number, // even though it comes from beneficiary, the account number will be in the provider form
    emailAlternate: provider?.email,
    firstName: provider?.name,
    lastName: '',
    tin: provider?.taxpayer_id,
    addressLine1: provider?.address.street_address,
    addressLine2: provider?.address.unit2,
    city: provider?.address.city,
    country: provider?.address.country,
    countryCode: provider?.address.country,
    regionCode: provider?.address.region,
    postalCode: provider?.address.postal_code,
    region: provider?.address.region,
    usAccreditedCategory: profile.us_accredited_category,
    caAccreditedCategory: profile.ca_accredited_investor,
    complete: profile.complete,
    confirmation: provider?.confirmation === '1',
    beneficiary: writable<BaseEntity>(beneficiaryBody),
  };
};
